import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MdArrowBack, MdDownload, MdSensors, MdSpeed, MdAccessTime, MdTune, MdLock } from 'react-icons/md';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { format, subDays } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';

const CustomTooltip = ({ active, payload, label, unit }) => {
    if (active && payload && payload.length) {
        return (
            <div style={{
                background: 'var(--bg-elevated)', border: '1px solid var(--border)',
                borderRadius: 10, padding: '10px 14px', fontSize: 13
            }}>
                <p style={{ color: 'var(--text-secondary)', marginBottom: 4 }}>{label}</p>
                <p style={{ color: 'var(--primary)', fontWeight: 700, fontSize: 15 }}>
                    {payload[0].value} <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' }}>{unit}</span>
                </p>
            </div>
        );
    }
    return null;
};

export default function CompanySensorPage() {
    const { sensorUUID } = useParams();
    const navigate = useNavigate();
    const { t, language } = useLanguage();
    const { user } = useAuth();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [startDate, setStartDate] = useState(format(subDays(new Date(), 1), 'yyyy-MM-dd'));
    const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [lastValue, setLastValue] = useState(null);
    const [currentSensor, setCurrentSensor] = useState(null);
    const [activeRange, setActiveRange] = useState(1); // default 1 day
    const [editingThreshold, setEditingThreshold] = useState(false);
    const [minThresholdInput, setMinThresholdInput] = useState('');
    const [maxThresholdInput, setMaxThresholdInput] = useState('');
    const [savingThreshold, setSavingThreshold] = useState(false);

    const fnsLocale = language === 'tr' ? tr : undefined;

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);

            // Fetch Primary Data (This now includes sensor/system metadata in the response)
            const dataRes = await api.get(`/sensor-data/${sensorUUID}`, {
                params: { startDate, endDate }
            });

            const rawData = dataRes.data.data || [];
            const chartData = rawData.map(d => ({
                time: format(new Date(d.timestamp), 'yyyy/MM/dd HH:mm', { locale: fnsLocale }),
                fullTime: format(new Date(d.timestamp), 'yyyy/MM/dd HH:mm:ss', { locale: fnsLocale }),
                value: d.value
            }));
            setData(chartData);
            if (dataRes.data.lastValue) setLastValue(dataRes.data.lastValue.value);

            // Populate metadata from the primary response payload
            if (dataRes.data.sensor) {
                const s = dataRes.data.sensor;
                setCurrentSensor({
                    ...s,
                    systemName: s.systemId?.systemName || t('common.unknownSystem')
                });
                setMinThresholdInput(s.minThreshold ?? '');
                setMaxThresholdInput(s.maxThreshold ?? '');
            }
        } catch (err) {
            console.error('Fetch error:', err);
            toast.error(t('sensorDetail.dataLoadFailed'));
        } finally {
            setLoading(false);
        }
    }, [sensorUUID, startDate, endDate, t, fnsLocale]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const setQuickRange = (days) => {
        setActiveRange(days);
        const end = new Date();
        const start = subDays(end, days);
        setStartDate(format(start, 'yyyy-MM-dd'));
        setEndDate(format(end, 'yyyy-MM-dd'));
    };

    const handleExport = async () => {
        try {
            setExporting(true);
            const res = await api.get('/sensor-data/export/excel', {
                params: { sensorUUID, startDate, endDate },
                responseType: 'blob'
            });
            const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const fileName = currentSensor
                ? `${currentSensor.sensorName}-${startDate}-${endDate}.xlsx`
                : `sensor-${sensorUUID.substring(0, 8)}-${startDate}-${endDate}.xlsx`;
            a.download = fileName;
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success(t('sensorDetail.excelDownloaded'));
        } catch {
            toast.error(t('sensorDetail.excelFailed'));
        } finally {
            setExporting(false);
        }
    };

    const handleSaveThreshold = async () => {
        if (minThresholdInput !== '' && Number.isNaN(Number(minThresholdInput))) { toast.error(t('sensorThresholdModal.invalidNumber')); return; }
        if (maxThresholdInput !== '' && Number.isNaN(Number(maxThresholdInput))) { toast.error(t('sensorThresholdModal.invalidNumber')); return; }
        if (minThresholdInput !== '' && maxThresholdInput !== '' && Number(minThresholdInput) >= Number(maxThresholdInput)) {
            toast.error(t('sensorThresholdModal.invalidRange'));
            return;
        }
        try {
            setSavingThreshold(true);
            await api.put(`/sensors/${currentSensor._id}/threshold`, {
                minThreshold: minThresholdInput === '' ? null : Number(minThresholdInput),
                maxThreshold: maxThresholdInput === '' ? null : Number(maxThresholdInput)
            });
            toast.success(t('sensorThresholdModal.updated'));
            setEditingThreshold(false);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.message || t('sensorThresholdModal.updateFailed'));
        } finally {
            setSavingThreshold(false);
        }
    };

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const reverseData = [...data].reverse();
    const totalPages = Math.ceil(reverseData.length / itemsPerPage);
    const paginatedData = reverseData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const minVal = data.length > 0 ? Math.min(...data.map(d => d.value)) : null;
    const maxVal = data.length > 0 ? Math.max(...data.map(d => d.value)) : null;

    return (
        <div className="page-wrapper">
            <div className="page-header">
                <div className="page-header-left">
                    <button className="btn btn-secondary btn-icon" onClick={() => navigate('/company')} id="back-btn" style={{ marginRight: 8 }}>
                        <MdArrowBack />
                    </button>
                    <div>
                        <h1 className="page-title">
                            {currentSensor ? currentSensor.sensorName : (loading ? t('sensorDetail.sensorLoading') : t('sensorDetail.title'))}
                        </h1>
                        <p className="page-subtitle" style={{ fontSize: 13, marginBottom: 2 }}>
                            {currentSensor ? currentSensor.systemName : (loading ? t('sensorDetail.systemInfoLoading') : '')}
                        </p>
                    </div>
                </div>
                <div className="page-header-actions">
                    {(user.isOwner || user.permissions?.canDownloadData) && (
                        <button className="btn btn-success btn-sm" onClick={handleExport} disabled={exporting} id="export-btn">
                            {exporting ? <span className="spinner spinner-sm" /> : <MdDownload />}
                            {t('sensorDetail.exportExcel')}
                        </button>
                    )}
                </div>
            </div>

            {/* Stats */}
            <div className="stats-grid">
                {[
                    { label: t('sensorDetail.lastValue'), value: lastValue !== null ? `${lastValue} ${currentSensor?.unit || ''}` : '—', icon: <MdSpeed />, color: 'var(--primary)', bg: 'rgba(79,142,247,0.15)' },
                    { label: t('sensorDetail.maximum'), value: maxVal !== null ? `${maxVal} ${currentSensor?.unit || ''}` : '—', icon: '↑', color: 'var(--success)', bg: 'rgba(16,185,129,0.15)' },
                    { label: t('sensorDetail.minimum'), value: minVal !== null ? `${minVal} ${currentSensor?.unit || ''}` : '—', icon: '↓', color: 'var(--danger)', bg: 'rgba(239,68,68,0.15)' },
                    { label: t('sensorDetail.dataCount'), value: data.length, icon: <MdAccessTime />, color: 'var(--warning)', bg: 'rgba(245,158,11,0.15)' },
                ].map((s, i) => (
                    <motion.div key={i} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }} className="stat-card">
                        <div className="stat-icon" style={{ background: s.bg, color: s.color }}>{s.icon}</div>
                        <div className="stat-info">
                            <div className="stat-value">{s.value}</div>
                            <div className="stat-label">{s.label}</div>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Threshold (only shown if the admin has defined at least one) */}
            {currentSensor && ((currentSensor.minThreshold !== null && currentSensor.minThreshold !== undefined) || (currentSensor.maxThreshold !== null && currentSensor.maxThreshold !== undefined)) && (
                <motion.div className="card" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 20, maxWidth: 420 }}>
                    <div className="card-header" style={{ padding: '14px 18px 12px' }}>
                        <div className="card-title" style={{ fontSize: 14 }}>
                            <div className="card-title-icon" style={{ width: 26, height: 26, fontSize: 14, background: 'rgba(139,92,246,0.15)', color: 'var(--secondary)' }}>
                                <MdTune />
                            </div>
                            {t('sensorDetail.thresholdTitle')}
                        </div>
                        {currentSensor.thresholdEditableByCompany && (user.isOwner || user.permissions?.canEditThreshold) && !editingThreshold && (
                            <button className="btn btn-secondary btn-sm" onClick={() => setEditingThreshold(true)}>{t('common.edit') || 'Düzenle'}</button>
                        )}
                    </div>
                    <div className="card-body" style={{ padding: '16px 18px' }}>
                        {editingThreshold ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                <div style={{ display: 'flex', gap: 10 }}>
                                    <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
                                        <label className="form-label">{t('sensorDetail.minThresholdLabel')}</label>
                                        <input type="number" className="form-input" value={minThresholdInput}
                                            onChange={(e) => setMinThresholdInput(e.target.value)} disabled={savingThreshold} />
                                    </div>
                                    <div className="form-group" style={{ marginBottom: 0, flex: 1 }}>
                                        <label className="form-label">{t('sensorDetail.maxThresholdLabel')}</label>
                                        <input type="number" className="form-input" value={maxThresholdInput}
                                            onChange={(e) => setMaxThresholdInput(e.target.value)} disabled={savingThreshold} />
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                                    <button className="btn btn-secondary btn-sm" onClick={() => {
                                        setEditingThreshold(false);
                                        setMinThresholdInput(currentSensor.minThreshold ?? '');
                                        setMaxThresholdInput(currentSensor.maxThreshold ?? '');
                                    }} disabled={savingThreshold}>{t('common.cancel')}</button>
                                    <button className="btn btn-primary btn-sm" onClick={handleSaveThreshold} disabled={savingThreshold}>
                                        {savingThreshold ? <span className="spinner spinner-sm" /> : t('sensorDetail.saveThreshold')}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div style={{ display: 'flex', gap: 18 }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>{t('sensorDetail.minThresholdLabel')}</div>
                                        <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--warning)' }}>
                                            {currentSensor.minThreshold !== null && currentSensor.minThreshold !== undefined ? `${currentSensor.minThreshold} ${currentSensor.unit}` : t('sensorDetail.thresholdNotSet')}
                                        </div>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>{t('sensorDetail.maxThresholdLabel')}</div>
                                        <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--danger)' }}>
                                            {currentSensor.maxThreshold !== null && currentSensor.maxThreshold !== undefined ? `${currentSensor.maxThreshold} ${currentSensor.unit}` : t('sensorDetail.thresholdNotSet')}
                                        </div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-muted)', marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--border)' }}>
                                    {!currentSensor.thresholdEditableByCompany && <MdLock size={12} />}
                                    {currentSensor.thresholdEditableByCompany ? t('sensorDetail.thresholdEditableNotice') : t('sensorDetail.thresholdReadOnlyNotice')}
                                </div>
                            </div>
                        )}
                    </div>
                </motion.div>
            )}

            {/* Filter */}
            <div className="filter-bar">
                <span className="filter-label">📅 {t('sensorDetail.dateRange')}:</span>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('sensorDetail.startDate')}</span>
                    <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} id="start-date" />
                </label>
                <span style={{ color: 'var(--text-muted)', alignSelf: 'flex-end', paddingBottom: 6 }}>→</span>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('sensorDetail.endDate')}</span>
                    <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} id="end-date" />
                </label>
                <button className="btn btn-secondary btn-sm" onClick={fetchData} id="filter-btn" style={{ alignSelf: 'flex-end' }}>{t('common.filter')}</button>

                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignSelf: 'flex-end' }}>
                    <button className={`btn btn-sm ${activeRange === 1 ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setQuickRange(1)}>{t('sensorDetail.daily')}</button>
                    <button className={`btn btn-sm ${activeRange === 7 ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setQuickRange(7)}>{t('sensorDetail.weekly')}</button>
                    <button className={`btn btn-sm ${activeRange === 30 ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setQuickRange(30)}>{t('sensorDetail.monthly')}</button>
                </div>
            </div>

            {/* Chart */}
            <motion.div className="card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <div className="card-header">
                    <div className="card-title">
                        <div className="card-title-icon" style={{ background: 'rgba(79,142,247,0.15)', color: 'var(--primary)' }}>
                            <MdSensors />
                        </div>
                        {t('sensorDetail.timeSeriesChart')}
                    </div>
                </div>
                <div className="card-body">
                    {loading ? (
                        <div className="loading-container"><div className="spinner"></div></div>
                    ) : data.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-state-icon">📊</div>
                            <div className="empty-state-title">{t('common.noData')}</div>
                            <div className="empty-state-desc">{t('sensorDetail.noDataInRange')}</div>
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height={320}>
                            <AreaChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#4f8ef7" stopOpacity={0.3} />
                                        <stop offset="95%" stopColor="#4f8ef7" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                                <XAxis dataKey="time" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                                <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} tickLine={false} axisLine={false} />
                                <Tooltip content={<CustomTooltip unit={currentSensor?.unit} />} />
                                {currentSensor?.minThreshold !== null && currentSensor?.minThreshold !== undefined && (
                                    <ReferenceLine y={currentSensor.minThreshold} stroke="#f59e0b" strokeDasharray="6 4" strokeWidth={1.5} label={{ value: t('sensorDetail.minThresholdLabel'), position: 'insideBottomLeft', fill: '#f59e0b', fontSize: 11 }} />
                                )}
                                {currentSensor?.maxThreshold !== null && currentSensor?.maxThreshold !== undefined && (
                                    <ReferenceLine y={currentSensor.maxThreshold} stroke="#ef4444" strokeDasharray="6 4" strokeWidth={1.5} label={{ value: t('sensorDetail.maxThresholdLabel'), position: 'insideTopLeft', fill: '#ef4444', fontSize: 11 }} />
                                )}
                                <Area type="monotone" dataKey="value" stroke="#4f8ef7" strokeWidth={2.5} fill="url(#grad)"
                                    dot={data.length < 30 ? { fill: '#4f8ef7', r: 3 } : false}
                                    activeDot={{ r: 5, fill: '#4f8ef7', stroke: 'white', strokeWidth: 2 }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </motion.div>

            {/* Data Table */}
            {data.length > 0 && (
                <motion.div className="card" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} style={{ marginTop: 20 }}>
                    <div className="card-header">
                        <div className="card-title">{t('sensorDetail.recentRecords')}</div>
                        <span className="badge badge-primary">{data.length} {t('common.records')}</span>
                    </div>
                    <div className="table-container">
                        <table className="table">
                            <thead><tr><th>{t('sensorDetail.dateTime')}</th><th>{t('common.value')}</th></tr></thead>
                            <tbody>
                                {paginatedData.map((d, i) => (
                                    <tr key={i}>
                                        <td style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 500 }}>{d.fullTime || d.time}</td>
                                        <td>
                                            <span style={{ fontWeight: 700, color: 'var(--primary)', fontSize: 20 }}>{d.value}</span>
                                            <span style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-muted)', marginLeft: 6 }}>
                                                {currentSensor?.unit}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="pagination" style={{ display: 'flex', justifyContent: 'center', padding: '15px', gap: '8px', borderTop: '1px solid var(--border)' }}>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                            >
                                {t('common.back')}
                            </button>
                            <div style={{ display: 'flex', alignItems: 'center', fontSize: 13, color: 'var(--text-muted)', padding: '0 10px' }}>
                                {t('common.page')} {currentPage} / {totalPages}
                            </div>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                            >
                                {t('common.forward')}
                            </button>
                        </div>
                    )}
                </motion.div>
            )}
        </div>
    );
}
